import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { getFeatureKeyFromPath, CHILD_TO_PARENT_KEY_MAP } from 'constants/menuConstants';

/**
 * ProtectedRoute – enforces route-level access control.
 *
 * Behavior:
 *  • Super admins (user_type !== 4) can visit every dashboard route freely.
 *  • Subusers (user_type === 4) are checked against user.permissions.
 *    – If the route matches a permission-gated feature that is NOT in their
 *      permissions, they are redirected to /dashboard with a toast notification.
 *    – Routes marked as "main" in menuConstants (e.g. Dashboard, Account details,
 *      Help, Logout) are always accessible.
 *    – If permissions haven't loaded yet (null), the component renders children
 *      optimistically (fail-open) to avoid a flash-redirect on first load.
 */
const ProtectedRoute = ({ children }) => {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const loggedInUser = useSelector((state) => state.userDetails);
    const { user } = loggedInUser || {};
    // Track last checked path to avoid duplicate redirects
    const lastCheckedPath = useRef(null);

    useEffect(() => {
        // Only enforce for subusers; pass-through for everyone else
        if (!user || user.user_type !== 4) return;

        // Permissions not loaded yet — wait for them
        if (!user.permissions) return;

        // Avoid re-running for the same path twice
        if (lastCheckedPath.current === pathname) return;
        lastCheckedPath.current = pathname;

        // Resolve the feature key for this path
        const featureKey = getFeatureKeyFromPath(pathname);
        console.log('[ProtectedRoute] Path:', pathname, 'FeatureKey:', featureKey);

        // Hardcoded restriction for Subusers route
        if (featureKey === 'subusers') {
            if (user.user_type !== 3) {
                enqueueSnackbar(
                    'You are not authorised to visit this page.',
                    { variant: 'warning', autoHideDuration: 3000 }
                );
                navigate('/dashboard', { replace: true });
                return;
            }
        }

        // Hardcoded restriction for Add-on Groups route
        if (featureKey === 'addon_groups') {
            if (user.user_type !== 3) {
                enqueueSnackbar(
                    'You are not authorised to visit this page.',
                    { variant: 'warning', autoHideDuration: 3000 }
                );
                navigate('/dashboard', { replace: true });
                return;
            }
        }

        // Hardcoded restriction for Raw Materials / Components & Resources (recipes)
        // routes — super admin only, regardless of subuser permissions.
        if (featureKey === 'raw_materials' || featureKey === 'recipes') {
            if (user.user_type !== 3) {
                enqueueSnackbar(
                    'You are not authorised to visit this page.',
                    { variant: 'warning', autoHideDuration: 3000 }
                );
                navigate('/dashboard', { replace: true });
                return;
            }
        }

        // No feature key → freely accessible route (e.g. /dashboard, /dashboard/help)
        if (!featureKey) return;

        const parentKey = CHILD_TO_PARENT_KEY_MAP[featureKey] || featureKey;
        const permissions = user.permissions;

        // Check if the feature is present in the user's permission map
        const hasParent = Boolean(permissions[parentKey]);
        const hasChild = parentKey !== featureKey
            ? Boolean(permissions[parentKey]?.[featureKey])
            : hasParent;

        const isAllowed = hasParent && (parentKey === featureKey || hasChild);
        console.log('[ProtectedRoute] ParentKey:', parentKey, 'hasParent:', hasParent, 'hasChild:', hasChild, 'isAllowed:', isAllowed);

        if (!isAllowed) {
            enqueueSnackbar(
                'You are not authorised to visit this page.',
                { variant: 'warning', autoHideDuration: 3000 }
            );
            navigate('/dashboard', { replace: true });
        }
    }, [pathname, user, navigate, enqueueSnackbar]);

    return children;
};

export default ProtectedRoute;
